import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../Servicios/supabaseClient.js';
import { useAuth } from '../../Context/AuthContext';
import { useNavigate } from 'react-router-dom';
import titulo from '../../Img/titulo.png';
import './Dashboard.css';
import InvoicesList from '../../Components/InvoicesList.jsx';
import SalesExport from '../../Components/SalesExport.jsx';
import AfipModal from '../../Components/AfipModal.jsx';
import ExternalAccountsModal from '../../Components/ExternalAccountsModal.jsx';
import TeamModal from '../../Components/TeamModal.jsx';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [plan, setPlan] = useState(null);
    const [activeFlowsCount, setActiveFlowsCount] = useState(0);
    const [executionsMonthCount, setExecutionsMonthCount] = useState(0);
    const [savedHoursMonth, setSavedHoursMonth] = useState(0); // Nuevo state para horas ahorradas

    const [chartSeries, setChartSeries] = useState([]); // Datos para el gráfico
    const [recentActivity, setRecentActivity] = useState([]); // Datos para la tabla de abajo

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [afipStatus, setAfipStatus] = useState(null);
    const [showAfipModal, setShowAfipModal] = useState(false);
    const [showExternalAccountsModal, setShowExternalAccountsModal] = useState(false);
    const [showTeamModal, setShowTeamModal] = useState(false);

    const kpis = [
        { label: "Flujos activos", value: activeFlowsCount },
        { label: "Ejecuciones del mes", value: executionsMonthCount },
        // { label: "Éxito", value: "0%" },
        { label: "Ahorro (hs/mes)", value: savedHoursMonth }, // Actualizado
    ];

    useEffect(() => {
        async function fetchDashboardData() {
            if (!user) return;
            setLoading(true);

            // Fetch AFIP Status separately
            const { data: afipData } = await supabase
                .from('afip_credentials')
                .select('status')
                .eq('user_id', user.id)
                .single();

            if (!afipData) {
                setAfipStatus('unconfigured');
            } else {
                setAfipStatus(afipData.status);
            }

            try {
                // Definir rango del mes actual para el filtro
                const now = new Date();
                const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
                const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString();

                // EJECUTAMOS 5 PETICIONES EN PARALELO
                const [profileData, workflowsData, logsData, invoicesData, executionsParamsData] = await Promise.all([
                    // A. Perfil y Suscripción
                    supabase.from('profiles').select('*, subscriptions(*, plans(*))').eq('id', user.id).single(),

                    // B. Conteo de Flujos Activos
                    supabase.from('workflows').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('is_active', true),

                    // C. Actividad Reciente (Últimos 5 logs)
                    supabase
                        .from('execution_logs')
                        .select('*')
                        .eq('user_id', user.id)
                        .order('executed_at', { ascending: false })
                        .limit(5),

                    // D. Facturas del mes (Para el gráfico)
                    supabase
                        .from('invoices')
                        .select('created_at') // Solo necesitamos la fecha
                        .eq('user_id', user.id)
                        .eq('status', 'completada') // Solo las exitosas
                        .gte('created_at', firstDay)
                        .lte('created_at', lastDay),

                    // E. Ejecuciones del Mes (Conteo y Horas Ahorradas)
                    // Traemos solo la columna 'horas_ahorradas' para sumar en cliente.
                    // Supabase devuelve 'count' también si lo pedimos con parámetros extras, pero aquí lo haremos manual o con .count
                    // Para ser eficientes, pedimos los datos para sumar las horas y usamos el length para el count? 
                    // No, length puede estar paginado max 1000. Mejor pedir count por separado o confiar en el length si es bajo volumen.
                    // Vamos a asumir volumen razonable y traer 'horas_ahorradas'. Si son muchos, mejor un RPC.
                    // Por ahora: Traemos 'horas_ahorradas' y usamos count exacto.
                    supabase
                        .from('execution_logs')
                        .select('horas_ahorradas', { count: 'exact' })
                        .eq('user_id', user.id)
                        .gte('executed_at', firstDay)
                        .lte('executed_at', lastDay)
                ]);

                // --- PROCESAMIENTO DE RESPUESTAS ---

                // 1. Perfil
                if (profileData.data) {
                    setProfile(profileData.data);
                    if (profileData.data.subscriptions) {
                        setSubscription(profileData.data.subscriptions);
                        setPlan(profileData.data.subscriptions.plans);
                    }
                }

                // 2. Flujos
                if (workflowsData.count !== null) setActiveFlowsCount(workflowsData.count);

                // 3. Actividad Reciente
                if (logsData.data) {
                    setRecentActivity(logsData.data);
                }

                // 4. Gráfico
                if (invoicesData.data) {
                    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                    const counts = new Array(daysInMonth).fill(0);

                    invoicesData.data.forEach(inv => {
                        const dayIndex = new Date(inv.created_at).getDate() - 1;
                        if (counts[dayIndex] !== undefined) {
                            counts[dayIndex]++;
                        }
                    });
                    setChartSeries(counts);
                }

                // 5. Conteo y Horas Ahorradas
                if (executionsParamsData.data) {
                    // Count
                    if (executionsParamsData.count !== null) {
                        setExecutionsMonthCount(executionsParamsData.count);
                    } else {
                        setExecutionsMonthCount(executionsParamsData.data.length);
                    }

                    // Suma de horas
                    const totalHours = executionsParamsData.data.reduce((acc, curr) => {
                        return acc + (Number(curr.horas_ahorradas) || 0);
                    }, 0);
                    // Redondear a 1 decimal si es necesario
                    setSavedHoursMonth(Math.round(totalHours * 10) / 10);
                }

            } catch (error) {
                console.error("Error cargando dashboard:", error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        }

        fetchDashboardData();
    }, [user]);

    const handleAfipSuccess = () => {
        setAfipStatus('pendiente_validacion');
    };

    const chartData = useMemo(() => {
        const now = new Date();
        const year = now.getFullYear();
        const monthIdx = now.getMonth();
        const last = new Date(year, monthIdx + 1, 0);
        const daysInMonth = last.getDate();

        const data = Array.from({ length: daysInMonth }, (_, i) => {
            return {
                day: i + 1,
                value: chartSeries[i] || 0
            };
        });

        const monthLabel = now.toLocaleString(undefined, { month: "long", year: "numeric" });

        return { data, monthLabel };
    }, [chartSeries]);

    const handleLogout = async () => {
        try {
            const { error } = await supabase.auth.signOut();
            if (error) throw error;
            navigate('/login');
        } catch (error) {
            console.error('Error al cerrar sesión:', error.message);
        }
    };

    if (loading) {
        return <div>Cargando datos del perfil...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className="dashPage">
            <header className="dashHeader">
                <div className="brandMini">
                    <img src={titulo} style={{ height: "3rem", margin: "0" }} alt="" />
                </div>
                <a className="btnGhost smHidden" style={{ color: "red" }} onClick={handleLogout}>
                    Cerrar Sesion
                </a>
            </header>

            <main className="dashMain">
                <section className="welcome">
                    <div className="welcomeText">
                        <h1 className="title">Hola, {profile?.nombre} 👋</h1>
                        <p className="subtitle">
                            Bienvenido a tu panel. Acá ves tu plan, métricas y la actividad de tus automatizaciones.
                        </p>

                        <div className="userGrid">
                            <InfoRow label="Nombre" value={profile?.nombre} />
                            <InfoRow label="Empresa" value={profile?.empresa} />
                            <InfoRow label="Email" value={user.email} />
                            <InfoRow label="CUIT" value={profile?.cuit} />
                            <div className="infoRow">
                                <span className="infoLabel">Conexión AFIP</span>
                                <div className="infoValue">
                                    {afipStatus === 'unconfigured' && (
                                        <button
                                            className="btnAlert"
                                            onClick={() => setShowAfipModal(true)}
                                        >
                                            ⚠ Configurar ahora
                                        </button>
                                    )}
                                    {afipStatus === 'pendiente_validacion' && (
                                        <span className="pill pillWarn">⏳ Validando credenciales...</span>
                                    )}
                                    {afipStatus === 'ok' && (
                                        <span className="pill pillOk">✅ Conectado</span>
                                    )}
                                    {afipStatus === 'error' && (
                                        <button
                                            className="pill pillErr"
                                            onClick={() => setShowAfipModal(true)}
                                            style={{ border: 'none', cursor: 'pointer' }}
                                        >
                                            ❌ Error (Reintentar)
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="infoRow">
                                <span className="infoLabel">Team</span>
                                <div className="infoValue">
                                    <button
                                        className="btnGhost"
                                        style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid var(--border)' }}
                                        onClick={() => setShowTeamModal(true)}
                                    >
                                        + Gestionar Team
                                    </button>
                                </div>
                            </div>
                            <div className="infoRow">
                                <span className="infoLabel">Cuentas linkeadas</span>
                                <div className="infoValue">
                                    <button
                                        className="btnGhost"
                                        style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid var(--border)' }}
                                        onClick={() => setShowExternalAccountsModal(true)}
                                    >
                                        + Agregar cuenta
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <PlanCard plan={plan} subscription={subscription} activeFlowsCount={activeFlowsCount} />
                </section>

                <section className="kpiSection">
                    {kpis.map((k) => (
                        <div key={k.label} className="kpiCard">
                            <div className="kpiValue">{k.value}</div>
                            <div className="kpiLabel">{k.label}</div>
                        </div>
                    ))}
                </section>

                <section className="chartSection">
                    <div className="chartHeader">
                        <h2 className="h2">Facturas generadas durante el mes</h2>
                        <span className="muted">{chartData.monthLabel}</span>
                    </div>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData.data}
                                margin={{
                                    top: 5,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                <XAxis dataKey="day" stroke="#888" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="#888" fontSize={12} tickLine={false} axisLine={false} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    cursor={{ stroke: '#ddd', strokeWidth: 1 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#7b2cbf"
                                    strokeWidth={3}
                                    dot={{ r: 4, fill: '#7b2cbf', strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 6 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                <section className="activitySection">
                    <h2 className="h2">Actividad reciente</h2>
                    <RecentTable data={recentActivity} />
                </section>
                <section className="activitySection" style={{ marginTop: '1rem' }}>
                    <h2 className="h2">Facturas recientes</h2>
                    <InvoicesList />
                </section>
                <section className="activitySection" style={{ marginTop: '1rem' }}>
                    <h2 className="h2">Exportar ventas (AFIP)</h2>
                    <p className="muted" style={{ marginBottom: '0.75rem' }}>
                        Genera VENTAS.txt y ALICUOTAS.txt (formato RG 3685, igual al export de Comprobantes en Línea) más un CSV, con tus comprobantes emitidos.
                    </p>
                    <SalesExport defaultCuit={profile?.cuit} />
                </section>
            </main>

            {showAfipModal && (
                <AfipModal
                    userId={user.id}
                    onClose={() => setShowAfipModal(false)}
                    onSuccess={handleAfipSuccess}
                />
            )}

            {showExternalAccountsModal && (
                <ExternalAccountsModal
                    onClose={() => setShowExternalAccountsModal(false)}
                    onSuccess={() => console.log('External account saved')}
                />
            )}

            {showTeamModal && (
                <TeamModal
                    onClose={() => setShowTeamModal(false)}
                />
            )}

            <footer className="dashFooter">
                <span>© {new Date().getFullYear()} QuantumNodo</span>
            </footer>
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="infoRow">
            <span className="infoLabel">{label}</span>
            <span className="infoValue">{value}</span>
        </div>
    );
}

function PlanCard({ plan, subscription, activeFlowsCount }) {
    if (!plan || !subscription) return null;

    const venc = new Date(subscription.vencimiento);
    const nice = venc.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

    // Calcular porcentaje de uso
    const usagePercent = Math.min(100, Math.round((activeFlowsCount / plan.max_flujos) * 100));

    const statusClass =
        subscription.status === "activo" ? "pillOk" : subscription.status === "Por vencer" ? "pillWarn" : "pillErr";

    return (
        <div className="planCard">
            <div className="planHeader">
                <div>
                    <span className="planLabel">Tu Plan Actual</span>
                    <h3 className="planTitle">{plan.name}</h3>
                </div>
                <div className={`pill ${statusClass}`}>{subscription.status}</div>
            </div>

            <div className="planBody">
                <div className="usageSection">
                    <div className="usageHeader">
                        <span className="usageLabel">Flujos Activos</span>
                        <span className="usageValue">{activeFlowsCount} / {plan.max_flujos}</span>
                    </div>
                    <div className="progressBarBg">
                        <div className="progressBarFill" style={{ width: `${usagePercent}%` }}></div>
                    </div>
                </div>

                <div className="planMeta">
                    <div className="metaItem">
                        <span className="metaLabel">Vencimiento</span>
                        <span className="metaValue">{nice}</span>
                    </div>
                    <div className="metaItem">
                        <span className="metaLabel">Precio</span>
                        <span className="metaValue">${plan.price_monthly}/mes</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RecentTable({ data }) {
    return (
        <div className="tableWrap">
            <table className="table">
                <thead>
                    <tr>
                        <th>Hora</th>
                        <th>Status</th>
                        <th>Detalle</th>
                    </tr>
                </thead>
                <tbody>
                    {data && data.length > 0 ? (data.map((r) => (
                        <tr key={r.id}>
                            <td>{new Date(r.executed_at).toLocaleString()}</td>
                            <td>
                                <span className={`pill ${r.status === 'success' || r.status === 'Ejecutado' ? 'pillOk' : 'pillErr'}`}>
                                    {r.status}
                                </span>
                            </td>
                            <td>{r.details || r.evento || '-'}</td>
                        </tr>
                    ))) : (
                        <tr>
                            <td colSpan="3" className="noData">
                                No hay datos recientes
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function Logo({ size = 20 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
            <circle cx="12" cy="12" r="10" fill="none" stroke="var(--primary)" strokeWidth="2" />
            <path d="M5 12h14M12 5v14" stroke="var(--accent)" strokeWidth="2" />
        </svg>
    );
}

export default Dashboard;
